import type { MatchEvent } from "@arcade-hockey/shared";
import { InputButton } from "@arcade-hockey/shared";
import { clamp01 } from "../math/Geometry.js";
import { type RngState, nextAngleOffset } from "../math/Random.js";
import { tuning } from "../state/tuning.js";
import type { PlayerState, PuckState } from "../state/types.js";

const QUICK_THRESHOLD_TICKS = 6;

type ShootInputView = {
  buttons: number;
};

/**
 * Apply one tick of shoot-button input. Returns a ShotReleased event when
 * a shot is fired, or null otherwise.
 *
 * Mutates `player.shootHoldTicks`. On release while holding the puck, mutates
 * `puck.vel`, `puck.mode`, `puck.ownerId`, `puck.noRecaptureUntilTick`,
 * and `player.hasPuck`.
 */
export function applyShootInput(
  player: PlayerState,
  puck: PuckState,
  input: ShootInputView,
  currentTick: number,
  rng: RngState,
): MatchEvent | null {
  const isHeld = (input.buttons & InputButton.Shoot) !== 0;
  const wasHeld = player.shootHoldTicks > 0;
  if (!player.hasPuck) {
    // Cannot shoot without the puck; reset hold.
    player.shootHoldTicks = 0;
    return null;
  }
  if (player.isProtecting) {
    player.shootHoldTicks = 0;
    return null;
  }

  // One-timer fast path: fires immediately on the first qualifying tick.
  if (
    isHeld &&
    player.receivedPassFromTick >= 0 &&
    currentTick - player.receivedPassFromTick <= tuning.shot.oneTimerWindowTicks
  ) {
    const oneTimerDirX = player.stickDir.x;
    const oneTimerDirY = player.stickDir.y;
    const oneTimerSpeed = tuning.shot.quickSpeed * (1 + tuning.shot.oneTimerSpeedBonus);
    const playerSpeed = Math.sqrt(player.vel.x * player.vel.x + player.vel.y * player.vel.y);
    const speedFactor = playerSpeed / tuning.player.maxSpeed;
    const oneTimerAccuracyRad = speedFactor * 0.1 + tuning.shot.oneTimerAccuracyPenalty;
    const oneTimerSpread = nextAngleOffset(rng, oneTimerAccuracyRad);
    const cos = Math.cos(oneTimerSpread);
    const sin = Math.sin(oneTimerSpread);
    const oneTimerRotatedX = oneTimerDirX * cos - oneTimerDirY * sin;
    const oneTimerRotatedY = oneTimerDirX * sin + oneTimerDirY * cos;
    puck.vel.x = oneTimerRotatedX * oneTimerSpeed;
    puck.vel.y = oneTimerRotatedY * oneTimerSpeed;
    puck.mode = "Shot";
    puck.ownerId = null;
    puck.lastTouchedBy = player.id;
    puck.noRecaptureUntilTick = currentTick + tuning.puck.noRecaptureTicks;
    player.hasPuck = false;
    player.possessionQuality = 0;
    player.shootHoldTicks = 0;
    player.receivedPassFromTick = -1;

    return {
      type: "ShotReleased",
      tick: currentTick,
      from: player.id,
      speed: oneTimerSpeed,
      charge: 1.0,
    };
  }

  if (isHeld) {
    player.shootHoldTicks = Math.min(player.shootHoldTicks + 1, tuning.shot.chargeMaxTicks);
    return null;
  }

  if (!wasHeld) return null;

  // Released: launch shot.
  const heldTicks = player.shootHoldTicks;
  player.shootHoldTicks = 0;

  const dirX = player.stickDir.x;
  const dirY = player.stickDir.y;

  let speed: number;
  if (heldTicks <= QUICK_THRESHOLD_TICKS) {
    speed = tuning.shot.quickSpeed;
  } else {
    const charge = clamp01(heldTicks / tuning.shot.chargeMaxTicks);
    speed = tuning.shot.quickSpeed + (tuning.shot.chargedSpeed - tuning.shot.quickSpeed) * charge;
  }

  // Small deterministic spread reflecting accuracy under movement/pressure.
  const playerSpeed = Math.sqrt(player.vel.x * player.vel.x + player.vel.y * player.vel.y);
  const speedFactor = playerSpeed / tuning.player.maxSpeed;
  const accuracyPenaltyRad = speedFactor * 0.1;
  const spread = nextAngleOffset(rng, accuracyPenaltyRad);
  const cos = Math.cos(spread);
  const sin = Math.sin(spread);
  const rotatedX = dirX * cos - dirY * sin;
  const rotatedY = dirX * sin + dirY * cos;

  puck.vel.x = rotatedX * speed;
  puck.vel.y = rotatedY * speed;
  puck.mode = "Shot";
  puck.ownerId = null;
  puck.lastTouchedBy = player.id;
  puck.noRecaptureUntilTick = currentTick + tuning.puck.noRecaptureTicks;
  player.hasPuck = false;
  player.possessionQuality = 0;

  const charge = clamp01(heldTicks / tuning.shot.chargeMaxTicks);
  return {
    type: "ShotReleased",
    tick: currentTick,
    from: player.id,
    speed,
    charge,
  };
}
